import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ISSUE_LABEL, STATUS_LABEL } from "../lib/api";
import { devLog } from "../lib/log";
import { KeyRound, LogOut, Loader2, Car, MapPin, Phone, RefreshCw, CheckCircle2, Play, Settings, ExternalLink, Copy, AlertTriangle, Building2, User, FileText, Lock, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";
import TwoFAGate from "../components/admin/TwoFAGate";
import BrandsSection from "../components/admin/BrandsSection";
import InvoicesList from "../components/admin/InvoicesList";
import VehiclePricesSection from "../components/admin/VehiclePricesSection";
import { settingsRequest, settingsApi } from "../lib/adminSettings";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);

  const fetchInvoices = async (requestId) => {
    try {
      const res = await api.get(`/admin/requests/${requestId}/invoices`);
      setInvoices(res.data || []);
    } catch {
      setInvoices([]);
    }
  };

  useEffect(() => {
    if (selected?.id) fetchInvoices(selected.id);
    else setInvoices([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);


  const load = async () => {
    try {
      const [r, p] = await Promise.all([
        api.get("/admin/requests"),
        api.get("/admin/pricing-rules"),
      ]);
      setRequests(r.data);
      setPricing(p.data);
    } catch (e) {
      if (e.response?.status === 401) { navigate("/admin/login"); return; }
      toast.error("Error al cargar datos");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // Verifica sesión vía cookie con /admin/me; si falla, redirige al login.
    api.get("/admin/me").then(load).catch(() => navigate("/admin/login"));
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch (e) { devLog.warn("logout call failed:", e); }
    navigate("/admin/login");
  };

  const startJob = async (id) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/requests/${id}/start`);
      toast.success("Trabajo iniciado");
      await load();
    } catch { toast.error("Error"); }
    finally { setActionLoading(false); }
  };

  const completeJob = async (id) => {
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/requests/${id}/complete`);
      if (res.data.final_payment_url) {
        navigator.clipboard?.writeText(res.data.final_payment_url);
        toast.success("Enlace de pago final copiado al portapapeles");
        setSelected({ ...selected, _finalUrl: res.data.final_payment_url });
      } else {
        toast.success("Trabajo marcado como completado");
      }
      await load();
    } catch { toast.error("Error"); }
    finally { setActionLoading(false); }
  };

  const updatePrice = async (issue_type, base_price) => {
    try {
      await api.put("/admin/pricing-rules", { issue_type, base_price: Number(base_price) });
      toast.success("Precio actualizado");
      await load();
    } catch { toast.error("Error"); }
  };

  const pendingCount = requests.filter(r => r.status === "pending").length;
  const activeCount = requests.filter(r => ["deposit_paid", "in_progress"].includes(r.status)).length;
  const revenueToday = requests.filter(r => r.deposit_paid).reduce((a,b) => a + (b.final_paid ? b.estimated_price : b.deposit_amount), 0);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Toaster theme="dark" richColors position="top-center" />
      {/* Topbar */}
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#FFC107] flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-white">SasCerrajería</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] font-mono">Panel admin</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="btn-outline px-3 py-2 rounded-md text-sm flex items-center gap-2" data-testid="refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Actualizar</span>
            </button>
            <button onClick={logout} className="btn-outline px-3 py-2 rounded-md text-sm flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat label="Solicitudes" value={requests.length} />
          <Stat label="Pendientes" value={pendingCount} highlight />
          <Stat label="Activas" value={activeCount} />
          <Stat label="Ingresos" value={`${revenueToday.toFixed(0)}€`} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 mb-6">
          {[
            { k: "requests", t: "Solicitudes" },
            { k: "pricing", t: "Precios base" },
            { k: "settings", t: "Configuración", icon: Lock },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${tab === t.k ? "text-[#FFC107] border-[#FFC107]" : "text-[#71717A] border-transparent hover:text-white"}`}
              data-testid={`tab-${t.k}`}
            >
              {t.icon && <t.icon className="w-3.5 h-3.5" />}
              {t.t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#FFC107] animate-spin" /></div>
        ) : tab === "requests" ? (
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-3">
              {requests.length === 0 && <p className="text-[#71717A] text-sm">Sin solicitudes aún.</p>}
              {requests.map((r) => (
                <button key={r.id} onClick={() => setSelected(r)}
                  className={`w-full sc-card rounded-md p-5 text-left ${selected?.id === r.id ? "!border-[#FFC107]" : ""}`}
                  data-testid={`request-row-${r.id}`}
                >
                  <div className="flex justify-between items-start gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="font-display font-semibold text-lg truncate">{r.customer_name}</div>
                        {r.customer_type === "empresa" && (
                          <Building2 className="w-3.5 h-3.5 text-[#FFC107] flex-shrink-0" title="Empresa" />
                        )}
                        {r.is_custom_quote && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-[#FFC107] text-black flex-shrink-0">Otro</span>
                        )}
                      </div>
                      <div className="text-sm text-[#A1A1AA] truncate">{ISSUE_LABEL[r.issue_type]} · {r.vehicle_brand} {r.vehicle_model} {r.vehicle_year}</div>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#71717A] font-mono">{new Date(r.created_at).toLocaleString("es-ES")}</span>
                    <span className="font-display font-semibold text-[#FFC107]">{(r.final_agreed_price || r.estimated_price).toFixed(2)}€</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Detail panel */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 sc-card rounded-md p-5">
                {!selected ? (
                  <p className="text-sm text-[#71717A]">Selecciona una solicitud para ver detalles.</p>
                ) : (
                  <>
                    <div className="text-xs font-mono uppercase tracking-wider text-[#71717A] mb-2">#{selected.id.slice(0,8)}</div>
                    <h3 className="font-display font-bold text-2xl mb-1">{selected.customer_name}</h3>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <StatusBadge status={selected.status} />
                      {selected.customer_type === "empresa" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider bg-[#FFC107]/10 text-[#FFC107]">
                          <Building2 className="w-3 h-3" /> Empresa {selected.tax_id ? `· ${selected.tax_id}` : ""}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider bg-white/5 text-[#A1A1AA]">
                          <User className="w-3 h-3" /> Particular
                        </span>
                      )}
                    </div>

                    {selected.is_custom_quote && (
                      <div className="mt-3 mb-1 p-3 rounded-md bg-[#FFC107]/10 border border-[#FFC107]/30 flex gap-2 items-start">
                        <AlertTriangle className="w-4 h-4 text-[#FFC107] flex-shrink-0 mt-0.5" />
                        <div className="text-xs">
                          <div className="font-semibold text-[#FFC107] mb-0.5 uppercase tracking-wider">Presupuesto in situ</div>
                          <div className="text-[#A1A1AA]">El cliente solo ha pagado el desplazamiento. Evalúa el problema al llegar y registra el precio final acordado abajo.</div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3 mt-5 text-sm">
                      <Field icon={Phone} label="Teléfono" value={selected.customer_phone} />
                      <Field icon={Car} label="Vehículo" value={`${selected.vehicle_brand} ${selected.vehicle_model} · ${selected.vehicle_year}`} />
                      <Field icon={MapPin} label="Dirección" value={selected.location?.address} />
                      {selected.location?.lat && <div className="text-xs font-mono text-[#71717A] pl-6">GPS: {selected.location.lat.toFixed(5)}, {selected.location.lng.toFixed(5)} · <a target="_blank" rel="noopener noreferrer" className="text-[#FFC107] hover:underline" href={`https://www.google.com/maps?q=${selected.location.lat},${selected.location.lng}`}>Ver mapa ↗</a></div>}
                      {selected.notes && <Field icon={FileText} label={selected.is_custom_quote ? "Problema descrito" : "Notas"} value={selected.notes} />}
                    </div>

                    <div className="border-t border-white/10 my-5" />

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-[#A1A1AA]">Estimado (IVA inc.)</span><span className="font-mono">{selected.estimated_price.toFixed(2)}€</span></div>
                      {selected.final_agreed_price && (
                        <div className="flex justify-between"><span className="text-[#FFC107]">Precio final acordado</span><span className="font-mono text-[#FFC107] font-semibold">{selected.final_agreed_price.toFixed(2)}€</span></div>
                      )}
                      <div className="flex justify-between"><span className="text-[#A1A1AA]">Depósito</span><span className={`font-mono ${selected.deposit_paid ? "text-[#10B981]" : "text-[#71717A]"}`}>{selected.deposit_amount.toFixed(2)}€ {selected.deposit_paid ? "✓" : ""}</span></div>
                      <div className="flex justify-between"><span className="text-[#A1A1AA]">Pendiente</span><span className={`font-mono ${selected.final_paid ? "text-[#10B981]" : "text-[#71717A]"}`}>{(((selected.final_agreed_price || selected.estimated_price) - (selected.deposit_paid ? selected.deposit_amount : 0))).toFixed(2)}€ {selected.final_paid ? "✓" : ""}</span></div>
                    </div>

                    {/* Facturas emitidas */}
                    <InvoicesList invoices={invoices} />

                    {/* Final agreed price input - shown when job is in progress / deposit paid / custom quote */}
                    {!selected.final_paid && (selected.is_custom_quote || ["deposit_paid", "in_progress"].includes(selected.status)) && (
                      <FinalPriceInput selected={selected} onSaved={load} />
                    )}

                    <div className="mt-5 space-y-2">
                      {selected.status === "deposit_paid" && (
                        <button onClick={() => startJob(selected.id)} disabled={actionLoading}
                          className="w-full btn-amber py-2.5 rounded-md font-semibold flex items-center justify-center gap-2 text-sm"
                          data-testid="start-job-btn">
                          <Play className="w-4 h-4" /> Iniciar trabajo
                        </button>
                      )}
                      {(selected.status === "in_progress" || selected.status === "deposit_paid") && !selected.final_paid && (
                        <button onClick={() => completeJob(selected.id)} disabled={actionLoading}
                          className="w-full btn-outline py-2.5 rounded-md font-semibold flex items-center justify-center gap-2 text-sm"
                          data-testid="complete-job-btn">
                          <CheckCircle2 className="w-4 h-4" /> Completar y generar cobro final
                        </button>
                      )}
                      {selected._finalUrl && (
                        <div className="p-3 rounded-md bg-[#FFC107]/5 border border-[#FFC107]/20 text-xs break-all">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[#FFC107] font-semibold uppercase tracking-wider text-[10px]">Enlace cobro final</span>
                            <button onClick={() => { navigator.clipboard?.writeText(selected._finalUrl); toast.success("Copiado"); }}><Copy className="w-3.5 h-3.5 text-[#FFC107]" /></button>
                          </div>
                          <a href={selected._finalUrl} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FFC107] flex items-center gap-1">{selected._finalUrl.substring(0,42)}... <ExternalLink className="w-3 h-3" /></a>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : tab === "pricing" ? (
          <div className="space-y-3 max-w-2xl">
            <p className="text-sm text-[#A1A1AA] mb-4">Ajusta los precios base por tipo de avería. Los recargos y el IVA se configuran en la pestaña <b className="text-white">Configuración</b> (requiere verificación 2FA).</p>
            {pricing.map((p) => (
              <PricingRow key={p.issue_type} rule={p} onSave={updatePrice} />
            ))}
          </div>
        ) : (
          <SettingsTab />
        )}
      </main>
    </div>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div className="sc-card rounded-md p-4">
    <div className="text-xs font-mono uppercase tracking-wider text-[#71717A]">{label}</div>
    <div className={`font-display font-bold text-3xl mt-1 ${highlight ? "text-[#FFC107]" : "text-white"}`}>{value}</div>
  </div>
);

const Field = ({ icon: Icon, label, value }) => (
  <div className="flex gap-3">
    <Icon className="w-4 h-4 text-[#71717A] mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <div className="text-xs font-mono uppercase tracking-wider text-[#71717A]">{label}</div>
      <div className="text-sm break-words">{value}</div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending: { c: "bg-white/10 text-[#A1A1AA]", t: "Pendiente" },
    deposit_paid: { c: "bg-[#10B981]/10 text-[#10B981]", t: "Depósito pagado" },
    in_progress: { c: "bg-[#FFC107]/10 text-[#FFC107]", t: "En curso" },
    completed: { c: "bg-[#10B981]/10 text-[#10B981]", t: "Completado" },
    fully_paid: { c: "bg-[#10B981]/10 text-[#10B981]", t: "Pagado total" },
    closed: { c: "bg-white/10 text-[#A1A1AA]", t: "Cerrado" },
  };
  const s = map[status] || map.pending;
  return <span className={`inline-block px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider ${s.c}`}>{s.t}</span>;
};

const PricingRow = ({ rule, onSave }) => {
  const [val, setVal] = useState(rule.base_price);
  return (
    <div className="sc-card rounded-md p-4 flex items-center gap-4">
      <div className="flex-1">
        <div className="font-display font-semibold">{ISSUE_LABEL[rule.issue_type] || rule.issue_type}</div>
        <div className="text-xs font-mono text-[#71717A] uppercase tracking-wider">{rule.issue_type}</div>
      </div>
      <input type="number" step="0.01" value={val} onChange={(e) => setVal(e.target.value)}
        className="w-28 bg-[#141414] border border-white/10 rounded-md px-3 py-2 text-right focus:border-[#FFC107] outline-none"
        data-testid={`price-input-${rule.issue_type}`} />
      <button onClick={() => onSave(rule.issue_type, val)} className="btn-amber px-4 py-2 rounded-md font-semibold text-sm"
        data-testid={`price-save-${rule.issue_type}`}>Guardar</button>
    </div>
  );
};

const FinalPriceInput = ({ selected, onSaved }) => {
  const [price, setPrice] = useState(selected.final_agreed_price || "");
  const [saving, setSaving] = useState(false);
  // Reset cuando cambia la selección
  useEffect(() => { setPrice(selected.final_agreed_price || ""); }, [selected.id, selected.final_agreed_price]);
  const save = async () => {
    if (!price || Number(price) < 0) return toast.error("Introduce un precio válido");
    setSaving(true);
    try {
      await api.put(`/admin/requests/${selected.id}/final-price`, { final_agreed_price: Number(price) });
      toast.success("Precio final guardado. Al pulsar 'Completar' se cobrará la diferencia.");
      await onSaved?.();
    } catch {
      toast.error("Error al guardar");
    } finally { setSaving(false); }
  };
  return (
    <div className="mt-5 p-4 rounded-md bg-[#FFC107]/5 border border-[#FFC107]/20">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-[#FFC107]" />
        <span className="text-xs font-mono uppercase tracking-wider text-[#FFC107] font-semibold">Precio final acordado (con IVA)</span>
      </div>
      <p className="text-xs text-[#A1A1AA] mb-3 leading-relaxed">
        Introduce el total que has acordado con el cliente (servicio + desplazamiento + IVA 21%). Cuando pulses &laquo;Completar&raquo;, Stripe cobrará la diferencia respecto al depósito.
      </p>
      <div className="flex gap-2">
        <input
          type="number" step="0.01" min="0"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="Ej: 320.50"
          className="flex-1 bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-white focus:border-[#FFC107] outline-none"
          data-testid="final-price-input"
        />
        <span className="self-center text-[#A1A1AA]">€</span>
        <button
          onClick={save}
          disabled={saving}
          className="btn-amber px-4 py-2.5 rounded-md font-semibold text-sm flex items-center gap-2"
          data-testid="final-price-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
        </button>
      </div>
      {selected.deposit_paid && Number(price) > 0 && (
        <p className="text-xs text-[#71717A] mt-2 font-mono">
          Pendiente de cobro: <span className="text-white">{(Number(price) - selected.deposit_amount).toFixed(2)}€</span>
        </p>
      )}
    </div>
  );
};

// ============== SETTINGS TAB (con verificación 2FA) ==============

function SettingsTab() {
  const [verified, setVerified] = useState(null); // null=checking, true/false
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await settingsRequest("get", "/admin/settings");
      setData(res.data);
      setVerified(true);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        setVerified(false);
      } else {
        toast.error("Error al cargar configuración");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { loadSettings(); /* eslint-disable-next-line */ }, []);

  const lockSettings = async () => {
    try { await settingsApi.lock(); } catch (e) { devLog.warn("lock settings failed:", e); }
    setVerified(false);
    setData(null);
  };

  if (verified === null || (verified && loading && !data)) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#FFC107] animate-spin" /></div>;
  }
  if (!verified) return <TwoFAGate onVerified={() => { setVerified(true); loadSettings(); }} />;
  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#FFC107] animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <ShieldCheck className="w-4 h-4 text-[#10B981]" />
          <span className="text-[#10B981]">Verificación activa</span>
          <span className="text-[#71717A]">· caduca en 30 min</span>
        </div>
        <button
          onClick={lockSettings}
          className="text-xs text-[#71717A] hover:text-white"
          data-testid="lock-settings-btn"
        >
          Bloquear
        </button>
      </div>

      <GlobalSettingsForm initial={data.global} onSaved={loadSettings} />
      <VehiclePricesSection />
      <BrandsSection brands={data.brands} onChange={loadSettings} />
      <ServicePricingSection rules={data.pricing_rules} onChange={loadSettings} />
    </div>
  );
}

function GlobalSettingsForm({ initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await settingsRequest("put", "/admin/settings/global", {
        price_per_km: Number(form.price_per_km),
        road_factor: Number(form.road_factor),
        iva_rate: Number(form.iva_rate),
        company_discount: Number(form.company_discount),
        year_threshold: parseInt(form.year_threshold, 10),
        year_multiplier: Number(form.year_multiplier),
      });
      toast.success("Configuración global guardada");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };
  return (
    <div className="sc-card rounded-md p-6">
      <h3 className="font-display font-semibold text-lg mb-1">Tarifas globales</h3>
      <p className="text-xs text-[#71717A] mb-5">Estos valores se aplican a TODOS los cálculos de precio.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <SettingInput label="Precio por km (€)" value={form.price_per_km} onChange={(v) => update("price_per_km", v)} step="0.05" testid="set-price-km" />
        <SettingInput label="Factor distancia (carretera vs línea recta)" value={form.road_factor} onChange={(v) => update("road_factor", v)} step="0.05" testid="set-road-factor" />
        <SettingInput label="IVA (decimal, ej. 0.21)" value={form.iva_rate} onChange={(v) => update("iva_rate", v)} step="0.01" testid="set-iva" />
        <SettingInput label="Descuento empresa (decimal, ej. 0.10)" value={form.company_discount} onChange={(v) => update("company_discount", v)} step="0.01" testid="set-company-discount" />
        <SettingInput label="Año umbral 'vehículo reciente'" value={form.year_threshold} onChange={(v) => update("year_threshold", v)} step="1" testid="set-year-threshold" />
        <SettingInput label="Recargo año reciente (decimal, ej. 1.30 = +30%)" value={form.year_multiplier} onChange={(v) => update("year_multiplier", v)} step="0.05" testid="set-year-multiplier" />
      </div>
      <button onClick={save} disabled={saving} className="btn-amber mt-5 px-5 py-2.5 rounded-md font-semibold text-sm flex items-center gap-2" data-testid="save-global-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar tarifas globales"}
      </button>
    </div>
  );
}

function ServicePricingSection({ rules, onChange }) {
  const updateRule = async (issue_type, base_price) => {
    try {
      await settingsRequest("put", "/admin/pricing-rules", { issue_type, base_price: Number(base_price) });
      toast.success("Precio base actualizado");
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div className="sc-card rounded-md p-6">
      <h3 className="font-display font-semibold text-lg mb-1">Precios base por servicio</h3>
      <p className="text-xs text-[#71717A] mb-5">Precio del servicio antes de recargos por marca, año y desplazamiento.</p>
      <div className="space-y-2">
        {(rules || []).map((r) => <PricingRow key={r.issue_type} rule={r} onSave={updateRule} />)}
      </div>
    </div>
  );
}

function SettingInput({ label, value, onChange, step = "0.01", testid }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] mb-1.5 block">{label}</label>
      <input
        type="number" step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-white focus:border-[#FFC107] outline-none"
        data-testid={testid}
      />
    </div>
  );
}

